import { Injectable, inject } from '@angular/core';
import { currencyFloatToInteger, integerToCurrencyFloat } from '@acme/b2b/pricing';
import { ensureArray } from '@b2b/common/utils/coerce-array';
import {
  AutorenewInCart,
  CartFormItem,
  CartOrderForm,
  GiftCardRedemption,
  ShoppingCartItem,
  createAutorenewInCart,
} from '@b2b/types/cart';
import { TODO } from '@b2b/types/core/utils';
import { CartEventOrderForm } from '@b2b/types/event';
import { CartMembershipOrderForm, Membership, isMembershipOrderForm } from '@b2b/types/membership';
import { OrderContact } from '@b2b/types/order';
import { Organization } from '@b2b/types/organization';
import {
  deduplicateFormsByDisplaySettings,
  filterEventFormsBySalesChannel,
  filterMembershipFormsBySalesChannel,
} from '@b2b/views/application/views/cart/components/cart-forms/utils/form-filters.utils';
import { BehaviorSubject, combineLatest, first, firstValueFrom, map } from 'rxjs';
import { ApiService } from '../api/api.service';
import { CalendarService } from '../calendar/calendar.service';
import { HelpersService } from '../helpers/helpers.service';
import { ThemesHelperService } from '../themes-helper/themes-helper.service';
import { UserPreferencesService } from '../user-preferences/user-preferences.service';
import { IQPort, Q_PORT } from '../utils';
import { cartOrderFormToCartFormItem } from './cart.service.utils';
import { CartCouponsService } from './coupons/cart-coupons.service';
import { CartEventsService } from './events/cart-events.service';
import { CartMembershipsService } from './memberships/cart-memberships.service';

/**
 * CartService (Angular 18)
 *
 * Replaces: apps/b2b-ui/common/services/cart/cart.service.ts
 * Main cart orchestration service. Manages cart state, items, order contacts,
 * transacting member, and coordinates with CartCouponsService, CartEventsService,
 * and CartMembershipsService sub-services.
 *
 * ⚠️ PITFALL #7: cart.id can be null. Do NOT reset model.id after createCart() sets it.
 *
 * Registered via downgradeInjectable in ng-downgraded-components.ts as 'CartService'.
 * Wave 4 — Main chain.
 */

type CartStatus = 'idle' | 'loading';

interface CartState {
  status: CartStatus;
  autorenewInCart: AutorenewInCart;
}

function createInitialState(): CartState {
  return {
    status: 'idle',
    autorenewInCart: createAutorenewInCart(),
  };
}

@Injectable({ providedIn: 'root' })
export class CartService {
  private $q: IQPort;
  private apiService: ApiService;
  private helpersService: HelpersService;
  public coupons: CartCouponsService;
  public memberships: CartMembershipsService;
  public events: CartEventsService;
  private themesHelperService: ThemesHelperService;
  private userPreferencesService: UserPreferencesService;
  private calendarService: CalendarService;

  private stateSubject = new BehaviorSubject<CartState>(createInitialState());
  state$: ReturnType<typeof combineLatest>;

  constructor() {
    this.$q = inject<IQPort>(Q_PORT);
    this.apiService = inject(ApiService);
    this.helpersService = inject(HelpersService);
    this.coupons = inject(CartCouponsService);
    this.memberships = inject(CartMembershipsService);
    this.events = inject(CartEventsService);
    this.themesHelperService = inject(ThemesHelperService);
    this.userPreferencesService = inject(UserPreferencesService);
    this.calendarService = inject(CalendarService);

    this.state$ = combineLatest({
      status: this.stateSubject.pipe(map((state) => state.status)),
      autorenewInCart: this.stateSubject.pipe(map((state) => state.autorenewInCart)),
      memberships: this.memberships.state$,
    });
  }

  model = {
    items: [],
    id: null,
    couponCodes: null,
    membershipIds: null,
    membershipCategoryId: null,
    verifyEntitlements: true,
    forms: [],
    giftCardRedemptions: [],
  } as {
    items: TODO[];
    id: TODO | null;
    couponCodes: TODO | null;
    membershipIds: TODO | null;
    membershipCategoryId: TODO | null;
    verifyEntitlements: boolean;
    orderContact?: OrderContact | null;
    reservationId?: TODO;
    forms: CartFormItem[];
    giftCardRedemptions?: GiftCardRedemption[];
  };

  itemCount = 0;
  initted = false;
  orderContact: Partial<OrderContact> | null = {};
  organization: Partial<Organization> | null = {};
  transactingMember: Partial<{ member: TODO; membership: TODO; memberships: TODO; membershipCategoryId: TODO }> | null =
    null;
  forms: CartOrderForm[] = [];

  private formsCache: { promise: Promise<CartOrderForm[]> | null; timestamp: number | null } = {
    promise: null,
    timestamp: null,
  };

  readonly CODES = {
    errors: {
      SHOPPING_CART_DNE: 1033,
    },
  };

  init(): this {
    if (!this.initted) {
      this.memberships = this.memberships.init(this);
      this.coupons = this.coupons.init(this);
      this.events = this.events.init(this);
      this.getCart();
    }
    return this;
  }

  getSummary(options = {}): { data: TODO[]; options: TODO } {
    return {
      data: this.memberships.getModel().concat(this.events.getEvents()),
      options: Object.assign({ editable: true, removable: true }, options),
    };
  }

  setForms(forms: CartOrderForm[]): void {
    this.forms = forms;
  }

  setFormResponseId(props: { formId: string; formResponseId: string }): void {
    const formItem = this.model.forms.find((f) => f.formId === props.formId);
    if (formItem) {
      formItem.formResponseId = props.formResponseId;
    }
  }

  getAllForms(): Promise<CartOrderForm[]> {
    if (this.formsCache.promise) {
      return this.formsCache.promise;
    }

    const eventForms = this.events.getEventForms();

    this.formsCache.promise = this.memberships.getForms().then((membershipForms) => {
      this.formsCache.timestamp = Date.now();
      const allForms = [...eventForms, ...membershipForms];

      const memberships = this.memberships.model as Membership[];
      const filteredForms = deduplicateFormsByDisplaySettings([
        ...filterEventFormsBySalesChannel(
          allForms.filter((f): f is CartEventOrderForm => f.itemType === 'event'),
          'insideSales',
        ),
        ...filterMembershipFormsBySalesChannel(
          allForms.filter((f): f is CartMembershipOrderForm => isMembershipOrderForm(f)),
          memberships,
          'insideSalesIndividual',
        ),
      ]);

      this.setForms(filteredForms);

      const missingForms = filteredForms.filter((form) => !(this.model.forms ?? []).some((f) => f.formId === form.id));
      if (missingForms.length) {
        const newItems: CartFormItem[] = missingForms.map(cartOrderFormToCartFormItem);
        this.model.forms = [...(this.model.forms ?? []), ...newItems];
        this.updateCart();
      }

      return filteredForms;
    });

    return this.formsCache.promise;
  }

  getEventFormIds(eventId: string): Promise<string[]> {
    return this.getAllForms().then((forms) =>
      forms.flatMap((f) => (f.itemType === 'event' && (f as CartEventOrderForm).event?.id === eventId ? [f.id] : [])),
    );
  }

  getMembershipFormIds(membershipCategoryId: string): Promise<string[]> {
    return this.getAllForms().then((forms) =>
      forms.flatMap((f) =>
        isMembershipOrderForm(f) &&
        f.membershipLevelsInfo?.some((info: TODO) => info.membershipLevel.id === membershipCategoryId)
          ? [f.id]
          : [],
      ),
    );
  }

  removeModelFormsByIds(formIds: string[]): void {
    this.model.forms = (this.model.forms ?? []).filter((f) => !formIds.includes(f.formId));
    this.forms = this.forms.filter((f) => !formIds.includes(f.id));
    this.invalidateFormsCache();
  }

  private invalidateFormsCache(): void {
    this.formsCache.promise = null;
    this.formsCache.timestamp = null;
    this.forms = [];
  }

  getOrderContact(): OrderContact | null {
    return this.model.orderContact?.customerId ? this.model.orderContact : null;
  }

  getOrderContactFullName(): string {
    const orderContact = this.model.orderContact;
    if (!orderContact) return '';
    return [orderContact.prefix, orderContact.firstName, orderContact.middleName, orderContact.lastName]
      .filter((name) => !!name)
      .join(' ');
  }

  getOrderContactType(): string {
    return 'Member';
  }

  getSaveModel(): TODO {
    let model = JSON.parse(JSON.stringify(this.model));
    const membershipIds = this.getTransactingMembershipId();

    model.items.forEach((v: TODO) => {
      v.couponCodes = null;
      delete v.couponCodes;
    });

    this.setTransactingMembershipCategory();
    model.membershipCategoryId = this.getTransactingMembershipCategory();
    model.membershipIds = membershipIds ? [membershipIds] : [];
    model = this.clearCouponsWhenAllItemsAreRemoved(model);

    // giftCardRedemptions is a server-owned, read-only field (redemptions are
    // managed via the storedvalue endpoint). Never echo it back on cart save —
    // the ShoppingCart VO expects a different shape and rejects the payload.
    delete model.giftCardRedemptions;

    return model;
  }

  clearCouponsWhenAllItemsAreRemoved(cartModel: TODO): TODO {
    if (cartModel.items.length === 0) {
      cartModel.couponCodes = [];
    }
    return cartModel;
  }

  getModel(): typeof this.model {
    return this.model;
  }

  setModel(cart: TODO): void {
    const reservationId = this.model.reservationId;
    this.model = cart;
    this.invalidateFormsCache();

    if (this.model) {
      if (this.model.id) {
        if (!reservationId) {
          this.events
            .getReservation()
            .then((response: TODO) => {
              this.events.setReservation(response.payload);
            })
            .catch(() => {
              this.events.setReservation();
            });
        } else {
          this.setReservation(reservationId);
        }
      }

      if (this.model.couponCodes instanceof Array) {
        this.coupons.setModel(this.model.couponCodes);
      } else {
        this.coupons.resetModel();
      }

      if (this.model.items instanceof Array) {
        const memberships = this.model.items.filter((item) => item.itemType.toLowerCase().indexOf('membership') > -1);
        this.memberships.setModel(memberships);

        const events = this.model.items.filter((item) => {
          const itemType = item.itemType.toLowerCase();
          return itemType.indexOf('event') > -1 || itemType.indexOf('inventory') > -1;
        });
        this.events.setModel(events);

        this.itemCount = this.model.items.reduce((count, item) => {
          let quantity = parseInt(item.quantity, 10);
          if (isNaN(quantity)) quantity = 0;
          return quantity + count;
        }, 0);
      }
    }
  }

  refreshModel(data: TODO): void {
    this.setModel(data);
    this.orderContact = this.getOrderContact();
    this.setTransactingMember(null);
    this.setTransactingMembershipCategory();
  }

  resetModel(): void {
    this.model = {
      items: [],
      id: null,
      couponCodes: null,
      membershipIds: null,
      membershipCategoryId: null,
      reservationId: null,
      verifyEntitlements: true,
      forms: [],
      giftCardRedemptions: [],
    };

    if (this.initted) {
      this.setModel(this.model);
      this.coupons.resetModel();
      this.memberships.resetModel();
      this.events.resetModel();
      this.orderContact = this.getOrderContact();
      this.updateOrganization(null);
      this.resetTransactingMember();
      this.memberships.clearCustomerData();
    }
  }

  processMembership(membership: TODO): TODO {
    if (membership.membershipExpirationDate) {
      membership.membershipExpirationDateDisplay = this.calendarService.toFormat(
        this.calendarService.parseDate(membership.membershipExpirationDate),
        this.userPreferencesService.getDateFormat('shortDate', 'dateFormat'),
      );
    }
    return membership;
  }

  getMember(id: string): Promise<any> {
    return this.apiService.membership.cardholders.detail(id);
  }

  getMembership(id: number): Promise<any> {
    return this.apiService.membership.detail(id);
  }

  getMembershipSuccess(response: TODO): void {
    if (this.transactingMember == null) {
      this.transactingMember = {};
    }
    this.transactingMember.membership = this.processMembership(response.data.payload);
  }

  getMembershipError(_response: TODO): void {
    if (this.transactingMember == null) {
      this.transactingMember = {};
    }
    this.transactingMember.memberships = [];
  }

  getTransactingMemberId(): string | null {
    if (this.transactingMember && this.transactingMember.member) {
      return this.transactingMember.member.id;
    }
    return null;
  }

  getTransactingMembershipId(): string | null {
    const allowTicketTransactionOnExpiredMemberships = this.themesHelperService.getCompany(
      'allowTicketTransactionOnExpiredMemberships',
    );
    if (this.transactingMember && this.transactingMember.member) {
      const isExpiredMembership = this.memberships.isMembershipExpired(this.transactingMember.member);
      if (!isExpiredMembership || allowTicketTransactionOnExpiredMemberships) {
        return this.transactingMember.member.membershipId;
      }
    }
    return null;
  }

  hasTransactingMembershipCategory(categoryId: TODO): boolean {
    const transactingMembershipCategory = this.getTransactingMembershipCategory();
    return !!(transactingMembershipCategory && transactingMembershipCategory === categoryId);
  }

  getTransactingMembershipCategory(): TODO {
    return this.transactingMember ? this.transactingMember.membershipCategoryId : null;
  }

  setTransactingMembershipCategory(): void {
    const membershipLevelIds = this.memberships.getDiscountMembershipLevelIds(true);
    const membershipLevelId = membershipLevelIds.length ? membershipLevelIds[0] : null;
    if (this.transactingMember) {
      this.transactingMember.membershipCategoryId = membershipLevelId;
    } else {
      this.transactingMember = { membershipCategoryId: membershipLevelId };
    }
  }

  getTransactingMembershipNumber(): string | null {
    if (this.transactingMember && this.transactingMember.membership) {
      return this.transactingMember.membership.acmeMembershipNumber;
    }
    return null;
  }

  getTransactingMembership(): TODO {
    return this.transactingMember && this.transactingMember.membership;
  }

  getTransactingMember(): TODO {
    return this.transactingMember && this.transactingMember.member;
  }

  setTransactingMember(member: TODO): void {
    const contact = this.getOrderContact();
    const cardId = contact ? contact.cardId : null;
    const updateTransactingMember = (member: TODO) => {
      if (!this.transactingMember) {
        this.transactingMember = {};
      }
      this.transactingMember.member = member;
      this.getMembership(member.membershipId)
        .then((response) => {
          this.getMembershipSuccess(response);
        })
        .catch((response) => {
          this.getMembershipError(response);
        });
    };

    if (cardId && this.getTransactingMemberId() !== cardId) {
      if (member) {
        updateTransactingMember(member);
      } else {
        this.getMember(cardId)
          .then((response) => {
            updateTransactingMember(response.data.payload);
          })
          .catch(() => {
            this.resetTransactingMember();
          });
      }
    }

    if (!cardId) {
      this.resetTransactingMember();
    }
  }

  resetTransactingMember(): void {
    this.transactingMember = null;
    this.model.membershipIds = null;
  }

  setItems(items: TODO[], resetCoupons: boolean): void {
    this.model.items = items;
    if (resetCoupons) {
      this.coupons.resetModel();
    }
  }

  getItems(): TODO[] {
    return this.model.items || [];
  }

  getItem(itemId: TODO): TODO {
    const items = this.getItems().filter((item) => item.itemId === itemId);
    return items.length ? items[0] : null;
  }

  addItem(item: ShoppingCartItem): void {
    this.model.items = ensureArray(this.model.items);
    this.model.items.push(item);
    this.itemCount = this.model.items.reduce((count, item) => {
      let quantity = parseInt(item.quantity, 10);
      if (isNaN(quantity)) quantity = 0;
      return quantity + count;
    }, 0);
  }

  updateItem(item: TODO): void {
    const items = this.getItems();
    for (let i = 0, ii = items.length; i < ii; i++) {
      if (item.itemId === items[i].itemId) {
        items[i] = item;
        break;
      }
    }
    this.itemCount = this.model.items.reduce((count, item) => {
      let quantity = parseInt(item.quantity, 10);
      if (isNaN(quantity)) quantity = 0;
      return quantity + count;
    }, 0);
  }

  removeItem(itemId: TODO, skipGetCart = false): Promise<TODO> {
    let index = -1;
    const items = this.getItems();
    const q = this.$q.defer();

    for (let i = 0, ii = items.length; i < ii; i++) {
      if (itemId === items[i].itemId) {
        index = i;
        break;
      }
    }
    if (index > -1) {
      this.model.items.splice(index, 1);
    }

    this.memberships.setModel(
      this.model.items.filter((item) => item.itemType.toLowerCase().indexOf('membership') > -1),
    );
    this.events.setModel(
      this.model.items.filter((item) => {
        const itemType = item.itemType.toLowerCase();
        return itemType.indexOf('event') > -1 || itemType.indexOf('inventory') > -1;
      }),
    );

    if (!skipGetCart) {
      this.updateCart()
        .then((r) => q.resolve(r))
        .catch((r) => q.reject(r));
    } else {
      q.resolve();
      this.itemCount = this.model.items.reduce((count, item) => {
        let quantity = parseInt(item.quantity, 10);
        if (isNaN(quantity)) quantity = 0;
        return quantity + count;
      }, 0);
    }

    return q.promise as Promise<TODO>;
  }

  clearMembershipDiscountedEvents(): Promise<TODO> {
    const promises = this.events.getEvents().map((event) => this.events.removeDiscountedTickets(event, true));
    return this.$q.all(promises) as Promise<TODO>;
  }

  removeItems(itemIds: TODO[]): void {
    const remove = (itemId: TODO) => {
      let index = -1;
      const items = this.getItems();
      for (let i = 0, ii = items.length; i < ii; i++) {
        if (itemId === items[i].itemId) {
          index = i;
          break;
        }
      }
      if (index > -1) {
        this.model.items.splice(index, 1);
      }
    };
    itemIds.forEach((itemId) => remove(itemId));
  }

  getItemByType(itemType: TODO): TODO {
    const items = this.getItems().filter((item) => item.itemType === itemType);
    return items.length ? items[0] : null;
  }

  getMembershipItem(): TODO {
    const items = this.getItems().filter((item) => item.itemType.toLowerCase().indexOf('membership') > -1);
    return items.length ? items[0] : null;
  }

  getEventItemById(eventItem: TODO): TODO {
    const eventItemId = eventItem.itemId;
    const eventItemIsRetail = eventItem.isRetail || false;
    const items = this.getItems().filter((item) => {
      const isValidItemType =
        item.itemType.toLowerCase().indexOf('event') > -1 || item.itemType.toLowerCase().indexOf('inventory') > -1;
      const isRetail = item.isRetail || false;
      return isValidItemType && item.itemId === eventItemId && isRetail === eventItemIsRetail;
    });
    return items.length ? items[0] : null;
  }

  getItemRetailSubtotal(item: TODO): number {
    return integerToCurrencyFloat(item.quantity * currencyFloatToInteger(item.retailUnitPrice));
  }

  getItemDiscountedSubtotal(item: TODO): number {
    return integerToCurrencyFloat(item.quantity * currencyFloatToInteger(item.unitPrice));
  }

  isOrderContact(id: TODO): boolean {
    return !!(id && this.model.orderContact && this.model.orderContact.customerId == id);
  }

  hasOrderContact(): boolean {
    return !!(this.model.orderContact && this.model.orderContact.customerId);
  }

  setOrderContact(membership?: TODO): void {
    const membershipItem = membership || this.getMembershipItem();
    if (membershipItem) {
      this.model.orderContact = this.memberships.getOrderContact(
        membershipItem.membershipInfo.membershipCards || [],
        membershipItem.membershipInfo.orderContactId,
      );
    }
  }

  updateOrderContact(membership?: TODO): void {
    const membershipItem = membership || this.getMembershipItem();
    if (membershipItem) {
      this.model.orderContact = this.memberships.getOrderContact(
        membershipItem.membershipInfo.membershipCards || [],
        membershipItem.membershipInfo.orderContactId,
      );
      if (this.model.orderContact) {
        (this.model.orderContact as any).cardId = (this.model.orderContact as any).id;
        if ((this.model.orderContact as any).membershipId) {
          this.setTransactingMember(this.model.orderContact);
        }
      } else {
        this.model.membershipIds = null;
      }
    } else {
      this.model.orderContact = null;
      this.model.membershipIds = null;
    }
  }

  updateOrganization(organization: Partial<Organization> | null): void {
    this.organization = organization;
  }

  getOrganization(): Partial<Organization> | null {
    return this.organization;
  }

  setReservation(reservationId: TODO): void {
    if (this.model) {
      this.model.reservationId = reservationId;
    }
  }

  getReservationId(): TODO {
    return this.model && this.model.reservationId ? this.model.reservationId : null;
  }

  isMemberCart(): TODO {
    return this.getTransactingMembershipId() || this.getTransactingMembershipCategory();
  }

  getCart(): Promise<any> {
    const q = this.$q.defer();
    this.apiService.cart
      .detail()
      .then((response: TODO) => {
        this.refreshModel(response.data.payload);
        this.initted = true;
        q.resolve(this.getModel());
      })
      .catch(() => {
        this.createCart()
          .then((response: TODO) => {
            // NOTE: Do NOT call resetModel() here — createCart() has already
            // populated model.id. Resetting would wipe it, causing PUT to /v2/api/carts/null.
            this.initted = true;
            q.resolve(response);
          })
          .catch((r: TODO) => q.reject(r));
      });
    return q.promise as Promise<TODO>;
  }

  createCart(cart?: TODO): Promise<any> {
    const q = this.$q.defer();
    this.apiService.cart
      .create(cart || { verifyEntitlements: true })
      .then((response: TODO) => {
        this.model.id = response.data.payload;
        q.resolve(this.getModel());
      })
      .catch((r: TODO) => q.reject(r));
    return q.promise as Promise<TODO>;
  }

  updateCart(): Promise<any> {
    this.setStatus('loading');
    return this.apiService.cart
      .update(this.getSaveModel())
      .then((response: TODO) => {
        this.refreshModel(response.data.payload);
        return response;
      })
      .catch((response: TODO) => {
        if (response.status === 404) {
          return this.createCart(this.getSaveModel());
        }
        throw response;
      })
      .then((cartResponse: TODO) => this.runCouponValidationsAndReturnCartResponse(cartResponse))
      .then((cartResponse: TODO) => {
        this.setStatus('idle');
        return cartResponse;
      });
  }

  runCouponValidationsAndReturnCartResponse(cartResponse: TODO): Promise<any> {
    const couponCodes: TODO = this.helpersService.path(cartResponse, ['data', 'payload', 'couponCodes'], []);
    const couponsModel = couponCodes.map((couponOrCode: TODO) => {
      return typeof couponOrCode === 'string' ? { code: couponOrCode } : couponOrCode.code;
    });
    this.coupons.setModel(couponsModel);
    return this.coupons
      .validate()
      .then(() => cartResponse)
      .catch(() => cartResponse);
  }

  cancelCart(): Promise<TODO> {
    const q = this.$q.defer();
    const cart = this.getModel();
    if (cart && cart.id) {
      this.apiService.cart
        .delete(cart.id)
        .then(() => {
          this.invalidateFormsCache();
          this.resetModel();
          this.createCart().then((r: TODO) => q.resolve(r));
        })
        .catch((r: TODO) => q.reject(r));
    }
    return q.promise as Promise<TODO>;
  }

  resetCart(skipReInit = false): void {
    this.invalidateFormsCache();
    this.resetModel();
    this.initted = false;
    if (!skipReInit) {
      this.init();
    }
  }

  addCartItem(item: ShoppingCartItem): Promise<any> {
    const q = this.$q.defer();
    const cart = this.getModel();
    if (cart?.id == null) {
      q.reject();
    } else {
      this.apiService.cart.item.add(cart.id, item).then((response) => {
        q.resolve(response);
      });
    }

    return q.promise as Promise<TODO>;
  }

  updateCartItem(item: ShoppingCartItem): Promise<any> {
    const q = this.$q.defer();
    const cart = this.getModel();
    if (cart?.id == null) {
      q.reject();
    } else {
      this.invalidateFormsCache();
      return this.apiService.cart.item.update(cart.id, item as TODO);
    }
    return q.promise as Promise<TODO>;
  }

  getTotal(): number {
    let total = 0;
    this.memberships.getModel().forEach((membership) => {
      total += currencyFloatToInteger(membership.unitPrice, { significant: 2 });
    });
    this.events.getEvents().forEach((event) => {
      total += this.getTicketAmount(event, false);
      total += this.getAddOnAmount(event, false);
    });
    return integerToCurrencyFloat(total, { significant: 2 });
  }

  getTicketAmount(event: TODO, retail = false): number {
    let result = 0;
    if (event.tickets instanceof Array) {
      event.tickets.forEach((ticket: TODO) => {
        if (ticket.quantity) {
          result += retail
            ? ticket.quantity * currencyFloatToInteger(ticket.retailUnitPrice, { significant: 2 })
            : ticket.quantity * currencyFloatToInteger(ticket.unitPrice, { significant: 2 });
        }
      });
    }
    return result;
  }

  getAddOnAmount(event: TODO, retail = false): number {
    let result = 0;
    if (event.addOns instanceof Array) {
      event.addOns.forEach((addOn: TODO) => {
        if (addOn.quantity) {
          result += addOn.quantity * currencyFloatToInteger(addOn.unitPrice, { significant: 2 });
        }
      });
    }
    return result;
  }

  getRetailTotal(): number {
    let retailTotal = 0;
    this.memberships.getModel().forEach((membership) => {
      retailTotal += currencyFloatToInteger(membership.retailUnitPrice, { significant: 2 });
    });
    this.events.getEvents().forEach((event) => {
      retailTotal += this.getTicketAmount(event, true);
      retailTotal += this.getAddOnAmount(event, true);
    });
    return integerToCurrencyFloat(retailTotal, { significant: 2 });
  }

  getSavings(): number {
    let retailTotal = 0;
    let total = 0;
    this.memberships.getModel().forEach((item) => {
      retailTotal += currencyFloatToInteger(item.retailUnitPrice, { significant: 2 });
      total += currencyFloatToInteger(item.unitPrice, { significant: 2 });
    });
    this.events.getEvents().forEach((event) => {
      retailTotal += this.getTicketAmount(event, true);
      retailTotal += this.getAddOnAmount(event, true);
      total += this.getTicketAmount(event);
      total += this.getAddOnAmount(event);
    });
    return integerToCurrencyFloat(retailTotal - total, { significant: 2 });
  }

  setVerifyEntitlements(ignoreLimits: boolean): void {
    this.model.verifyEntitlements = !ignoreLimits;
  }

  send(
    event:
      | { type: 'RESET' }
      | { type: 'STATUS_CHANGED'; params: Pick<CartState, 'status'> }
      | { type: 'AUTORENEW_IN_CART_CHANGED'; params: Pick<CartState, 'autorenewInCart'> },
  ): void {
    switch (event.type) {
      case 'RESET':
        this.memberships.send({ type: 'RESET' });
        this.stateSubject.next(createInitialState());
        break;
      case 'STATUS_CHANGED':
        this.stateSubject.next({ ...this.stateSubject.value, status: event.params.status });
        break;
      case 'AUTORENEW_IN_CART_CHANGED':
        this.stateSubject.next({
          ...this.stateSubject.value,
          autorenewInCart: createAutorenewInCart(event.params.autorenewInCart),
        });
        break;
      default:
        break;
    }
  }

  setStatus(status: CartStatus): void {
    this.send({ type: 'STATUS_CHANGED', params: { status } });
  }

  waitFor(targetStatus: CartStatus): Promise<any> {
    return firstValueFrom(this.state$.pipe(first((state) => state.status === targetStatus)));
  }

  setAutorenewInCart(autorenewInCart: AutorenewInCart): void {
    this.send({ type: 'AUTORENEW_IN_CART_CHANGED', params: { autorenewInCart } });
  }

  getSnapshot(): CartState {
    return this.stateSubject.value;
  }
}